import React, { useEffect, useState } from 'react';
import { Button, message, Modal, Spin, Table, Typography, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import excel from 'assets/images/icons/excel.svg';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;
const { Text } = Typography;

/**
 * Excel upload modal for Gross Rent data
 * Allows users to download a template, fill it out, and import the data
 */
const GrossRentExcelModal = ({ visible, onCancel, onApply, existingData = [] }) => {
	const [excelData, setExcelData] = useState([]);
	const [parsedData, setParsedData] = useState([]);
	const [showData, setShowData] = useState(false);
	const [loading, setLoading] = useState(false);

	// Reset data when modal opens/closes
	useEffect(() => {
		if (!visible) {
			setExcelData([]);
			setParsedData([]);
			setShowData(false);
		}
	}, [visible]);

	// Download template with existing data prefilled
	const handleDownloadTemplate = () => {
		try {
			setLoading(true);

			// Create template data
			const data = [];

			// Add header row
			data.push(['From', 'To', 'Amount', 'Remarks']);

			// Add existing data rows if available
			if (existingData && existingData.length > 0) {
				existingData.forEach((item) => {
					data.push([item.from || '', item.to || '', item.amount || '', item.remarks || '']);
				});
			} else {
				// Add a single empty row if no data
				data.push(['1', '', '', '']);
			}

			// Create workbook
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.aoa_to_sheet(data);

			// Set column widths
			ws['!cols'] = [
				{ wch: 8 }, // From column
				{ wch: 8 }, // To column
				{ wch: 12 }, // Amount column
				{ wch: 25 }, // Remarks column
			];

			// Add worksheet to workbook
			XLSX.utils.book_append_sheet(wb, ws, 'Gross Rent');

			// Generate file and download
			XLSX.writeFile(wb, 'Gross_Rent_Template.xlsx');

			message.success('Template downloaded successfully');
		} catch (error) {
			console.error('Error generating template:', error);
			message.error('Failed to generate template');
		} finally {
			setLoading(false);
		}
	};

	// Handle file upload
	const handleFileUpload = (file) => {
		setLoading(true);

		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				// Process Excel file
				const arrayBuffer = e.target.result;

				// Parse Excel file
				const wb = XLSX.read(arrayBuffer, { type: 'array' });
				const sheetName = wb.SheetNames[0];
				const worksheet = wb.Sheets[sheetName];
				const rawData = XLSX.utils.sheet_to_json(worksheet);

				// Save the raw data for display
				setExcelData(rawData);

				// Validate the data structure
				if (!validateExcelStructure(rawData)) {
					throw new Error('Invalid Excel structure. Please use the template provided.');
				}

				// Format data for the form
				const formattedData = processExcelData(rawData);

				// Store the formatted data for later use
				setParsedData(formattedData);

				// Show the data table
				setShowData(true);
				message.success(`${file.name} uploaded successfully`);
			} catch (error) {
				console.error('Error processing Excel file:', error);
				message.error(`Failed to process ${file.name}: ${error.message}`);
			} finally {
				setLoading(false);
			}
		};

		reader.onerror = () => {
			setLoading(false);
			message.error('Error reading file');
		};

		reader.readAsArrayBuffer(file);

		// Return false to prevent default upload
		return false;
	};

	// Validate excel structure
	const validateExcelStructure = (data) => {
		if (!data || data.length === 0) return false;

		// Check if the required columns exist
		const requiredColumns = ['From', 'To', 'Amount'];
		const firstRow = data[0];

		return requiredColumns.every(
			(col) => Object.prototype.hasOwnProperty.call(firstRow, col) || Object.prototype.hasOwnProperty.call(firstRow, col.toLowerCase())
		);
	};

	// Process excel data into the format needed for the form
	const processExcelData = (data) => {
		let lastFrom = 1;

		return data.map((row) => {
			// Get values, handling different possible column names
			const from = row['From'] || row['from'] || lastFrom;
			const to = row['To'] || row['to'] || '';
			const amount = row['Amount'] || row['amount'] || '';
			const remarks = row['Remarks'] || row['remarks'] || '';

			// Update lastFrom for the next row if needed
			lastFrom = parseInt(to) + 1 || lastFrom + 1;

			// Return formatted row
			return {
				from: from.toString(),
				to: to.toString(),
				amount: amount.toString(),
				remarks: remarks.toString(),
			};
		});
	};

	// Apply the parsed data
	const handleApply = () => {
		if (!parsedData.length) {
			message.warning('No data to apply');
			return;
		}

		// Validate the data before applying
		const validationResult = validateParsedData(parsedData);
		if (!validationResult.valid) {
			message.error(validationResult.message);
			return;
		}

		// Apply the formatted data to the main form
		onApply(parsedData);
		onCancel();
	};

	// Validate the parsed data before applying
	const validateParsedData = (data) => {
		// Check for empty required fields
		for (let i = 0; i < data.length; i++) {
			const row = data[i];

			// Skip validation for the first row's "from" since it's always set
			if (i !== 0 && (!row.from || row.from.trim() === '')) {
				return { valid: false, message: `Row ${i + 1}: "From" value is required` };
			}

			if (!row.to || row.to.trim() === '') {
				return { valid: false, message: `Row ${i + 1}: "To" value is required` };
			}

			if (!row.amount || row.amount.trim() === '') {
				return { valid: false, message: `Row ${i + 1}: "Amount" value is required` };
			}

			// Validate that To > From
			const fromVal = parseInt(row.from);
			const toVal = parseInt(row.to);

			if (isNaN(fromVal) || isNaN(toVal)) {
				return { valid: false, message: `Row ${i + 1}: "From" and "To" must be valid numbers` };
			}

			if (toVal <= fromVal) {
				return { valid: false, message: `Row ${i + 1}: "To" value must be greater than "From" value` };
			}

			// Validate sequence (From of current row should be To+1 of previous row)
			if (i > 0) {
				const prevRow = data[i - 1];
				const prevToVal = parseInt(prevRow.to);

				if (fromVal !== prevToVal + 1) {
					return {
						valid: false,
						message: `Row ${i + 1}: "From" value should be ${prevToVal + 1} to maintain sequence`,
					};
				}
			}
		}

		return { valid: true };
	};

	// Get columns for the table
	const getColumns = () => {
		return [
			{
				title: 'From',
				dataIndex: 'From',
				key: 'from',
				render: (text, record) => record['From'] || record['from'] || '-',
			},
			{
				title: 'To',
				dataIndex: 'To',
				key: 'to',
				render: (text, record) => record['To'] || record['to'] || '-',
			},
			{
				title: 'Amount',
				dataIndex: 'Amount',
				key: 'amount',
				render: (text, record) => record['Amount'] || record['amount'] || '-',
			},
			{
				title: 'Remarks',
				dataIndex: 'Remarks',
				key: 'remarks',
				render: (text, record) => record['Remarks'] || record['remarks'] || '-',
			},
		];
	};

	return (
		<Modal
			title="Import Gross Rent Data"
			open={visible}
			onCancel={onCancel}
			width={800}
			footer={[
				<Button key="download" onClick={handleDownloadTemplate} icon={<DownloadOutlined />}>
					<img src={excel} alt="excel" width={16} style={{ marginRight: '5px' }} />
					Download Template
				</Button>,
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<Button key="apply" type="primary" onClick={handleApply} disabled={!showData || excelData.length === 0}>
					Apply
				</Button>,
			]}
		>
			<Spin spinning={loading}>
				{!showData ? (
					<div>
						<div style={{ marginBottom: '16px' }}>
							<Text>
								Upload an Excel file with gross rent data. You can download a template using the button below, fill it out, and
								upload.
							</Text>
						</div>

						<Dragger
							name="file"
							beforeUpload={(file) => handleFileUpload(file)}
							showUploadList={false}
							accept=".xlsx, .xls"
							disabled={loading}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
							<p className="ant-upload-hint">Support for .xlsx and .xls files</p>
						</Dragger>
					</div>
				) : (
					<div>
						<div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
							<Text>Found {excelData.length} rows of data in the uploaded file.</Text>
							<Button
								onClick={() => {
									setExcelData([]);
									setParsedData([]);
									setShowData(false);
								}}
							>
								Upload Another File
							</Button>
						</div>

						<Table
							dataSource={excelData}
							columns={getColumns()}
							pagination={false}
							size="small"
							scroll={{ y: 300 }}
							rowKey={(record, index) => `${index}`}
						/>
					</div>
				)}
			</Spin>
		</Modal>
	);
};

export default GrossRentExcelModal;
